import type { ReactNode } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { PRIORITY_STYLES } from '../constants';
import type { StageVM } from '../types';

// Stage card header (DC §7.2). Clickable only in the complete state. The detail
// panel is passed as children and rendered when expanded.
export function StageCard({
  stage,
  active,
  expandable,
  expanded,
  showLoading,
  loadingMsg,
  showPriority,
  onToggle,
  children,
}: {
  stage: StageVM;
  active: boolean;
  expandable: boolean;
  expanded: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showPriority: boolean;
  onToggle: () => void;
  children?: ReactNode;
}) {
  const pr = stage.priority ? PRIORITY_STYLES[stage.priority] : null;

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${stage.accent}`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: active ? '0 0 0 2px var(--signal-blue-20)' : 'none',
      }}
    >
      <div
        onClick={() => expandable && onToggle()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '15px 18px',
          cursor: expandable ? 'pointer' : 'default',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--muted-foreground)', fontWeight: 500 }}>Stage {stage.num}</span>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--foreground)' }}>{stage.title}</span>
          </div>
          {!!stage.summary && (
            <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--muted-foreground)', margin: 0 }}>{stage.summary}</p>
          )}
        </div>

        {showLoading && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 26,
              padding: '0 10px',
              borderRadius: 7,
              background: 'var(--signal-blue-10)',
              border: '1px solid var(--signal-blue-20)',
              color: 'var(--signal-blue)',
              fontSize: 11,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
            {loadingMsg}
          </span>
        )}

        {showPriority && pr && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 28,
              padding: '0 11px',
              borderRadius: 8,
              background: pr.bg,
              border: `1px solid ${pr.border}`,
              color: pr.color,
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            <span style={{ textTransform: 'uppercase' }}>{stage.priority}</span>
            {stage.priorityReason && (
              <span style={{ fontWeight: 500, marginLeft: 8, opacity: 0.85 }}>{stage.priorityReason}</span>
            )}
          </span>
        )}

        {expandable && (
          <span
            style={{
              display: 'inline-flex',
              color: 'var(--muted-foreground)',
              flexShrink: 0,
              transition: 'transform var(--duration-base) var(--ease-standard)',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <ChevronDown style={{ width: 18, height: 18 }} />
          </span>
        )}
      </div>

      {expandable && expanded && (
        <div style={{ borderTop: '1px solid var(--border)' }}>{children}</div>
      )}
    </div>
  );
}
